import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { useAuth } from '../../contexts/AuthContext';
import {
  FileText,
  Plus,
  Sparkles,
  Loader2,
  Edit,
  Trash2,
  Eye,
  Search,
  RefreshCw,
  ExternalLink,
  Lightbulb,
  Zap,
  Clock,
  CheckCircle,
  XCircle,
  Settings,
  Share2,
  ImagePlus,
  Upload,
  Wand2,
  Send,
  Image as ImageIcon,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminBlog = () => {
  const { token, user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('posts');

  const [editDialog, setEditDialog] = useState(false);
  const [generateDialog, setGenerateDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);

  const [editPost, setEditPost] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    excerpt: '',
    meta_description: '',
    meta_keywords: '',
    featured_image: '',
    featured_media_id: '',
    featured_image_source: 'manual',
    status: 'draft',
  });

  const [suggestions, setSuggestions] = useState([]);
  const [genTopic, setGenTopic] = useState('');
  const [genRegion, setGenRegion] = useState('Zürcher Oberland');
  const [generating, setGenerating] = useState(false);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);

  const [autoSettings, setAutoSettings] = useState({ auto_blog_enabled: false, auto_blog_interval_hours: 72 });
  const [autoLog, setAutoLog] = useState([]);
  const [savingAuto, setSavingAuto] = useState(false);
  const [triggering, setTriggering] = useState(false);

  const [socialPosts, setSocialPosts] = useState([]);
  const [mediaAssets, setMediaAssets] = useState([]);
  const [socialForm, setSocialForm] = useState({
    post_id: '',
    platforms: ['facebook'],
    caption: '',
    image_mode: 'post_featured',
    media_asset_id: '',
    auto_publish: false,
    campaign_name: '',
    scheduled_for: '',
    max_retries: 0,
  });
  const [submittingSocial, setSubmittingSocial] = useState(false);
  const [socialActionId, setSocialActionId] = useState('');

  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadTitle, setUploadTitle] = useState('');
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const [imagePrompt, setImagePrompt] = useState('');
  const [generatingImage, setGeneratingImage] = useState(false);

  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const jsonHeaders = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
  const authHeaders = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (token) fetchAll();
  }, [token]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [postsRes, autoRes, logRes, socialRes, mediaRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/blog/posts`, { headers: authHeaders }),
        fetch(`${API_URL}/api/admin/blog/auto-settings`, { headers: authHeaders }),
        fetch(`${API_URL}/api/admin/blog/auto-log`, { headers: authHeaders }),
        fetch(`${API_URL}/api/admin/blog/social-posts`, { headers: authHeaders }),
        fetch(`${API_URL}/api/admin/blog/media`, { headers: authHeaders }),
      ]);

      if (postsRes.ok) {
        const data = await postsRes.json();
        setPosts(data.posts || []);
        setSocialForm((prev) => ({ ...prev, post_id: prev.post_id || data.posts?.[0]?.id || '' }));
      }
      if (autoRes.ok) setAutoSettings(await autoRes.json());
      if (logRes.ok) setAutoLog((await logRes.json()).logs || []);
      if (socialRes.ok) setSocialPosts((await socialRes.json()).items || []);
      if (mediaRes.ok) setMediaAssets((await mediaRes.json()).assets || []);
    } catch (error) {
      console.error(error);
      toast.error('Blog-Daten konnten nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  };

  const fetchSuggestions = async () => {
    setLoadingSuggestions(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/blog/topics`, { headers: authHeaders });
      if (res.ok) setSuggestions((await res.json()).suggestions || []);
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingSuggestions(false);
    }
  };

  const openNewPost = () => {
    setEditPost(null);
    setFormData({
      title: '',
      content: '',
      excerpt: '',
      meta_description: '',
      meta_keywords: '',
      featured_image: '',
      featured_media_id: '',
      featured_image_source: 'manual',
      status: 'draft',
    });
    setEditDialog(true);
  };

  const openEditPost = (post) => {
    setEditPost(post);
    setFormData({
      title: post.title || '',
      content: post.content || '',
      excerpt: post.excerpt || '',
      meta_description: post.meta_description || '',
      meta_keywords: post.meta_keywords || '',
      featured_image: post.featured_image || '',
      featured_media_id: post.featured_media_id || '',
      featured_image_source: post.featured_image_source || 'manual',
      status: post.status || 'draft',
    });
    setEditDialog(true);
  };

  const handleImageSourceChange = (value) => {
    if (value === 'manual') {
      setFormData((prev) => ({ ...prev, featured_image_source: value, featured_media_id: '' }));
      return;
    }
    const asset = mediaAssets.find((item) => item.id === value);
    if (!asset) return;
    setFormData((prev) => ({
      ...prev,
      featured_image_source: asset.source_type,
      featured_media_id: asset.id,
      featured_image: asset.url,
    }));
  };

  const savePost = async () => {
    if (!formData.title.trim()) {
      toast.error('Titel ist erforderlich.');
      return;
    }
    setSaving(true);
    try {
      const url = editPost ? `${API_URL}/api/admin/blog/posts/${editPost.id}` : `${API_URL}/api/admin/blog/posts`;
      const method = editPost ? 'PUT' : 'POST';
      const res = await fetch(url, { method, headers: jsonHeaders, body: JSON.stringify(formData) });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Speichern fehlgeschlagen');
      }
      toast.success(editPost ? 'Beitrag aktualisiert.' : 'Beitrag erstellt.');
      setEditDialog(false);
      fetchAll();
    } catch (error) {
      toast.error(error.message || 'Fehler');
    } finally {
      setSaving(false);
    }
  };

  const deletePost = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/blog/posts/${deleteId}`, { method: 'DELETE', headers: authHeaders });
      if (!res.ok) throw new Error('Löschen fehlgeschlagen');
      toast.success('Beitrag gelöscht.');
      fetchAll();
    } catch (error) {
      toast.error(error.message || 'Fehler');
    } finally {
      setDeleteDialog(false);
      setDeleteId(null);
    }
  };

  const generateArticle = async () => {
    if (!genTopic.trim()) {
      toast.error('Bitte Thema eingeben.');
      return;
    }
    setGenerating(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/blog/generate`, {
        method: 'POST',
        headers: jsonHeaders,
        body: JSON.stringify({ topic: genTopic, region: genRegion }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Generierung fehlgeschlagen');
      toast.success(data.review?.ok === false ? 'Artikel generiert – bitte Review prüfen.' : 'Artikel generiert.');
      setGenerateDialog(false);
      setGenTopic('');
      fetchAll();
      openEditPost(data);
    } catch (error) {
      toast.error(error.message || 'Verbindungsfehler');
    } finally {
      setGenerating(false);
    }
  };

  const saveAutoSettings = async (enabled, interval) => {
    setSavingAuto(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/blog/auto-settings`, {
        method: 'POST',
        headers: jsonHeaders,
        body: JSON.stringify({ enabled, interval_hours: interval }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Einstellungen konnten nicht gespeichert werden');
      setAutoSettings((prev) => ({ ...prev, auto_blog_enabled: enabled, auto_blog_interval_hours: interval }));
      toast.success(data.message);
    } catch (error) {
      toast.error(error.message || 'Fehler');
    } finally {
      setSavingAuto(false);
    }
  };

  const triggerAutoGenerate = async () => {
    setTriggering(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/blog/trigger-auto`, { method: 'POST', headers: authHeaders });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Trigger fehlgeschlagen');
      toast.success(data.message);
      setTimeout(() => fetchAll(), 45000);
    } catch (error) {
      toast.error(error.message || 'Fehler');
    } finally {
      setTriggering(false);
    }
  };

  const uploadMedia = async () => {
    if (!selectedFile) {
      toast.error('Bitte zuerst ein Bild wählen.');
      return;
    }
    setUploadingMedia(true);
    try {
      const payload = new FormData();
      payload.append('file', selectedFile);
      payload.append('title', uploadTitle || selectedFile.name);
      const res = await fetch(`${API_URL}/api/admin/blog/media/upload`, {
        method: 'POST',
        headers: authHeaders,
        body: payload,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Upload fehlgeschlagen');
      toast.success('Bild zur Mediathek hinzugefügt.');
      setSelectedFile(null);
      setUploadTitle('');
      fetchAll();
    } catch (error) {
      toast.error(error.message || 'Upload fehlgeschlagen');
    } finally {
      setUploadingMedia(false);
    }
  };

  const generateMedia = async () => {
    if (!imagePrompt.trim()) {
      toast.error('Bitte Prompt eingeben.');
      return;
    }
    setGeneratingImage(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/blog/media/generate`, {
        method: 'POST',
        headers: jsonHeaders,
        body: JSON.stringify({ prompt: imagePrompt, title: 'KI-Bild aus Admin' }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'KI-Bild konnte nicht erzeugt werden');
      toast.success('KI-Bild erzeugt und zur Mediathek hinzugefügt.');
      setImagePrompt('');
      fetchAll();
    } catch (error) {
      toast.error(error.message || 'KI-Bild konnte nicht erzeugt werden');
    } finally {
      setGeneratingImage(false);
    }
  };

  const createSocialDrafts = async () => {
    if (!socialForm.post_id || socialForm.platforms.length === 0) {
      toast.error('Bitte Beitrag und Plattform wählen.');
      return;
    }
    setSubmittingSocial(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/blog/social-posts`, {
        method: 'POST',
        headers: jsonHeaders,
        body: JSON.stringify({ ...socialForm, origin_url: API_URL }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Social-Draft konnte nicht erstellt werden');
      toast.success('Social-Drafts erstellt.');
      setSocialForm((prev) => ({ ...prev, caption: '', media_asset_id: '', campaign_name: '', scheduled_for: '', max_retries: 0 }));
      fetchAll();
    } catch (error) {
      toast.error(error.message || 'Fehler');
    } finally {
      setSubmittingSocial(false);
    }
  };

  const runSocialAction = async (id, action, payload = null) => {
    setSocialActionId(id);
    try {
      const res = await fetch(`${API_URL}/api/admin/blog/social-posts/${id}/${action}`, {
        method: 'POST',
        headers: jsonHeaders,
        body: payload ? JSON.stringify(payload) : JSON.stringify({}),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Aktion fehlgeschlagen');
      toast.success(action === 'approve' ? 'Freigegeben.' : action === 'publish' ? 'Veröffentlichung gestartet.' : 'Abgelehnt.');
      fetchAll();
    } catch (error) {
      toast.error(error.message || 'Fehler');
    } finally {
      setSocialActionId('');
    }
  };

  const deleteMedia = async (id) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/blog/media/${id}`, { method: 'DELETE', headers: authHeaders });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Bild konnte nicht gelöscht werden');
      toast.success('Bild entfernt.');
      fetchAll();
    } catch (error) {
      toast.error(error.message || 'Löschen fehlgeschlagen');
    }
  };

  const filteredPosts = useMemo(() => posts.filter((post) => `${post.title} ${post.excerpt || ''} ${post.author || ''}`.toLowerCase().includes(searchTerm.toLowerCase())), [posts, searchTerm]);

  const publishedCount = posts.filter((post) => post.status === 'published').length;
  const draftCount = posts.filter((post) => post.status === 'draft').length;
  const autoCount = posts.filter((post) => post.auto_generated).length;
  const pendingApprovals = socialPosts.filter((post) => post.status === 'pending_approval').length;

  return (
    <div className="space-y-6" data-testid="admin-blog-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[hsl(var(--foreground))]">Blog / SEO</h1>
          <p className="text-sm text-[hsl(var(--muted-foreground))]">Beiträge, Bildquellen, Social Freigaben und Automatik zentral verwalten</p>
        </div>
        <div className="flex gap-3 flex-wrap">
          <Button variant="outline" onClick={() => { setGenerateDialog(true); fetchSuggestions(); }} data-testid="blog-generate-btn">
            <Sparkles className="w-4 h-4 mr-2" /> KI-Artikel
          </Button>
          <Button onClick={openNewPost} data-testid="blog-new-btn">
            <Plus className="w-4 h-4 mr-2" /> Neuer Beitrag
          </Button>
        </div>
      </div>

      <div className="flex gap-1 border-b border-[hsl(var(--border))] flex-wrap">
        {[
          { id: 'posts', label: 'Beiträge', icon: FileText },
          { id: 'social', label: 'Social Media', icon: Share2 },
          { id: 'media', label: 'Mediathek', icon: ImagePlus },
          { id: 'auto', label: 'Auto-Blog', icon: Zap },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px ${activeTab === tab.id ? 'border-[hsl(var(--primary))] text-[hsl(var(--primary))]' : 'border-transparent text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]'}`}
            data-testid={`blog-tab-${tab.id}`}
          >
            <tab.icon className="w-4 h-4" /> {tab.label}
            {tab.id === 'social' && pendingApprovals > 0 && <Badge className="ml-1">{pendingApprovals}</Badge>}
          </button>
        ))}
      </div>

      {activeTab === 'posts' && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card><CardContent className="pt-5 pb-4 flex items-center gap-3"><FileText className="w-8 h-8 text-[hsl(var(--primary))]" /><div><p className="text-2xl font-bold">{posts.length}</p><p className="text-xs text-[hsl(var(--muted-foreground))]">Gesamt</p></div></CardContent></Card>
            <Card><CardContent className="pt-5 pb-4 flex items-center gap-3"><Eye className="w-8 h-8 text-emerald-500" /><div><p className="text-2xl font-bold">{publishedCount}</p><p className="text-xs text-[hsl(var(--muted-foreground))]">Veröffentlicht</p></div></CardContent></Card>
            <Card><CardContent className="pt-5 pb-4 flex items-center gap-3"><Edit className="w-8 h-8 text-amber-500" /><div><p className="text-2xl font-bold">{draftCount}</p><p className="text-xs text-[hsl(var(--muted-foreground))]">Entwürfe</p></div></CardContent></Card>
            <Card><CardContent className="pt-5 pb-4 flex items-center gap-3"><Zap className="w-8 h-8 text-indigo-500" /><div><p className="text-2xl font-bold">{autoCount}</p><p className="text-xs text-[hsl(var(--muted-foreground))]">Auto-generiert</p></div></CardContent></Card>
          </div>

          <Card>
            <CardHeader>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <CardTitle>Beiträge</CardTitle>
                <div className="relative w-72 max-w-full">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[hsl(var(--muted-foreground))]" />
                  <Input placeholder="Suchen..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" data-testid="blog-search" />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin" /></div>
              ) : filteredPosts.length === 0 ? (
                <div className="text-center py-10 text-[hsl(var(--muted-foreground))]">
                  <FileText className="w-10 h-10 mx-auto mb-2 opacity-50" />
                  <p>Noch keine Beiträge.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {filteredPosts.map((post) => (
                    <div key={post.id} className="flex items-center justify-between p-4 rounded-lg border border-[hsl(var(--border))] hover:bg-[hsl(var(--accent)/0.3)] transition-colors gap-4 flex-wrap">
                      <div className="flex-1 min-w-[240px] mr-2">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <h3 className="font-medium text-[hsl(var(--foreground))]">{post.title}</h3>
                          <Badge variant={post.status === 'published' ? 'default' : 'secondary'} className="text-xs">{post.status === 'published' ? 'Live' : 'Entwurf'}</Badge>
                          {post.ai_generated && <Badge variant="outline" className="text-xs">KI</Badge>}
                          {post.featured_image_source && <Badge variant="outline" className="text-xs">Bild: {post.featured_image_source}</Badge>}
                        </div>
                        <p className="text-xs text-[hsl(var(--muted-foreground))]">{post.created_at ? new Date(post.created_at).toLocaleDateString('de-CH') : ''} · /blog/{post.slug}</p>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {post.status === 'published' && (
                          <Button variant="ghost" size="sm" asChild>
                            <a href={`/blog/${post.slug}`} target="_blank" rel="noopener noreferrer" data-testid={`blog-open-${post.id}`}><ExternalLink className="w-4 h-4" /></a>
                          </Button>
                        )}
                        <Button variant="ghost" size="sm" onClick={() => setSocialForm((prev) => ({ ...prev, post_id: post.id }))} data-testid={`blog-select-social-${post.id}`}>
                          <Share2 className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => openEditPost(post)} data-testid={`blog-edit-${post.id}`}><Edit className="w-4 h-4" /></Button>
                        <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700" onClick={() => { setDeleteId(post.id); setDeleteDialog(true); }} data-testid={`blog-delete-${post.id}`}><Trash2 className="w-4 h-4" /></Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {activeTab === 'social' && (
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
          <Card className="xl:col-span-1" data-testid="social-posting-config-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><Send className="w-5 h-5" /> Social Draft erstellen</CardTitle>
              <CardDescription>Admin-Freigabe und Bildquelle direkt beim Blogbeitrag wählen.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Blogbeitrag</Label>
                <Select value={socialForm.post_id} onValueChange={(value) => setSocialForm((prev) => ({ ...prev, post_id: value }))}>
                  <SelectTrigger data-testid="social-post-select"><SelectValue placeholder="Beitrag wählen" /></SelectTrigger>
                  <SelectContent>
                    {posts.map((post) => <SelectItem key={post.id} value={post.id}>{post.title}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Plattformen</Label>
                <div className="grid grid-cols-2 gap-2">
                  {['facebook', 'instagram'].map((platform) => {
                    const selected = socialForm.platforms.includes(platform);
                    return (
                      <button
                        key={platform}
                        type="button"
                        onClick={() => setSocialForm((prev) => ({
                          ...prev,
                          platforms: selected ? prev.platforms.filter((item) => item !== platform) : [...prev.platforms, platform],
                        }))}
                        className={`rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${selected ? 'border-[hsl(var(--primary))] bg-[hsl(var(--primary)/0.08)] text-[hsl(var(--primary))]' : 'border-[hsl(var(--border))] bg-white'}`}
                        data-testid={`social-platform-${platform}`}
                      >
                        {platform === 'facebook' ? 'Facebook' : 'Instagram'}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="space-y-2">
                <Label>Bildquelle</Label>
                <Select value={socialForm.image_mode} onValueChange={(value) => setSocialForm((prev) => ({ ...prev, image_mode: value }))}>
                  <SelectTrigger data-testid="social-image-mode"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="post_featured">Beitragsbild verwenden</SelectItem>
                    <SelectItem value="library">Bild aus Mediathek</SelectItem>
                    <SelectItem value="none">Ohne Bild (nur Facebook)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {socialForm.image_mode === 'library' && (
                <div className="space-y-2">
                  <Label>Mediathek-Bild</Label>
                  <Select value={socialForm.media_asset_id} onValueChange={(value) => setSocialForm((prev) => ({ ...prev, media_asset_id: value }))}>
                    <SelectTrigger data-testid="social-media-asset-select"><SelectValue placeholder="Bild wählen" /></SelectTrigger>
                    <SelectContent>
                      {mediaAssets.map((asset) => <SelectItem key={asset.id} value={asset.id}>{asset.title}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="space-y-2">
                <Label>Caption (optional)</Label>
                <Textarea value={socialForm.caption} onChange={(e) => setSocialForm((prev) => ({ ...prev, caption: e.target.value }))} rows={5} placeholder="Optionaler Text für Facebook / Instagram" data-testid="social-caption-input" />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Kampagnenname</Label>
                  <Input value={socialForm.campaign_name} onChange={(e) => setSocialForm((prev) => ({ ...prev, campaign_name: e.target.value }))} placeholder="z.B. Frühlingskampagne 2026" data-testid="social-campaign-name-input" />
                </div>
                <div className="space-y-2">
                  <Label>Planen für Datum / Uhrzeit</Label>
                  <Input type="datetime-local" value={socialForm.scheduled_for} onChange={(e) => setSocialForm((prev) => ({ ...prev, scheduled_for: e.target.value ? new Date(e.target.value).toISOString() : '' }))} data-testid="social-scheduled-for-input" />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Wiederholungsversuche pro Post</Label>
                <Select value={String(socialForm.max_retries)} onValueChange={(value) => setSocialForm((prev) => ({ ...prev, max_retries: parseInt(value, 10) }))}>
                  <SelectTrigger data-testid="social-max-retries-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {[0, 1, 2, 3, 5, 8].map((retry) => <SelectItem key={retry} value={String(retry)}>{retry} Versuche</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center justify-between rounded-lg border border-[hsl(var(--border))] px-4 py-3">
                <div>
                  <p className="font-medium text-sm">Automatisch veröffentlichen</p>
                  <p className="text-xs text-[hsl(var(--muted-foreground))]">Nur wenn Freigaben / Einstellungen es erlauben.</p>
                </div>
                <Switch checked={socialForm.auto_publish} onCheckedChange={(checked) => setSocialForm((prev) => ({ ...prev, auto_publish: checked }))} data-testid="social-auto-publish-toggle" />
              </div>

              <Button onClick={createSocialDrafts} disabled={submittingSocial} className="w-full" data-testid="social-create-draft-button">
                {submittingSocial ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                Draft / Queue erstellen
              </Button>
            </CardContent>
          </Card>

          <Card className="xl:col-span-2" data-testid="social-queue-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><Share2 className="w-5 h-5" /> Social Queue & Freigaben</CardTitle>
              <CardDescription>Admin-Freigaben, Fehler und erneute Veröffentlichungen an einem Ort.</CardDescription>
            </CardHeader>
            <CardContent>
              {socialPosts.length === 0 ? (
                <div className="text-center py-10 text-[hsl(var(--muted-foreground))]">
                  <Share2 className="w-10 h-10 mx-auto mb-3 opacity-50" />
                  <p>Noch keine Social-Entwürfe vorhanden.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {socialPosts.map((item) => (
                    <div key={item.id} className="rounded-xl border border-[hsl(var(--border))] p-4 flex flex-col gap-3" data-testid={`social-queue-item-${item.id}`}>
                      <div className="flex items-start justify-between gap-3 flex-wrap">
                        <div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-medium">{item.post_title}</p>
                            <Badge variant="outline">{item.platform}</Badge>
                            <Badge variant={item.status === 'published' ? 'default' : item.status === 'pending_approval' ? 'secondary' : 'outline'}>{item.status}</Badge>
                            {item.campaign_name && <Badge variant="outline">Kampagne: {item.campaign_name}</Badge>}
                          </div>
                          <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">{item.created_at ? new Date(item.created_at).toLocaleString('de-CH') : ''}</p>
                        </div>
                        <div className="flex gap-2 flex-wrap">
                          {item.status === 'pending_approval' && user?.role === 'admin' && (
                            <Button size="sm" onClick={() => runSocialAction(item.id, 'approve')} disabled={socialActionId === item.id} data-testid={`social-approve-${item.id}`}>
                              {socialActionId === item.id ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}Freigeben
                            </Button>
                          )}
                          {user?.role === 'admin' && (
                            <Button variant="outline" size="sm" onClick={() => runSocialAction(item.id, 'publish')} disabled={socialActionId === item.id} data-testid={`social-publish-${item.id}`}>
                              <Send className="w-4 h-4 mr-2" />Erneut senden
                            </Button>
                          )}
                          {user?.role === 'admin' && item.status !== 'published' && (
                            <Button variant="outline" size="sm" className="text-red-500" onClick={() => runSocialAction(item.id, 'reject', { reason: window.prompt('Ablehnungsgrund', 'Nicht freigegeben') || 'Nicht freigegeben' })} data-testid={`social-reject-${item.id}`}>
                              <XCircle className="w-4 h-4 mr-2" />Ablehnen
                            </Button>
                          )}
                        </div>
                      </div>
                      <p className="text-sm text-[hsl(var(--muted-foreground))] whitespace-pre-line">{item.caption}</p>
                      {(item.scheduled_for || item.max_retries) && (
                        <div className="text-xs text-[hsl(var(--muted-foreground))] flex flex-wrap gap-3">
                          {item.scheduled_for && <span>Geplant: {new Date(item.scheduled_for).toLocaleString('de-CH')}</span>}
                          <span>Retries: {item.retry_count || 0} / {item.max_retries || 0}</span>
                          {item.next_retry_at && <span>Nächster Retry: {new Date(item.next_retry_at).toLocaleString('de-CH')}</span>}
                        </div>
                      )}
                      {item.last_error && <p className="text-xs text-red-500" data-testid={`social-error-${item.id}`}>{item.last_error}</p>}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {activeTab === 'media' && (
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><Upload className="w-5 h-5" /> Eigenes Bild hochladen</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Titel</Label>
                <Input value={uploadTitle} onChange={(e) => setUploadTitle(e.target.value)} placeholder="z.B. Firmenfoto Frühlingskampagne" data-testid="media-upload-title" />
              </div>
              <div className="space-y-2">
                <Label>Datei</Label>
                <Input type="file" accept="image/*" onChange={(e) => setSelectedFile(e.target.files?.[0] || null)} data-testid="media-upload-file" />
              </div>
              <Button onClick={uploadMedia} disabled={uploadingMedia || !selectedFile} className="w-full" data-testid="media-upload-button">
                {uploadingMedia ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}Bild hochladen
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><Wand2 className="w-5 h-5" /> KI-Bild erzeugen</CardTitle>
              <CardDescription>Verwendet den OpenAI Key aus den Admin-Einstellungen.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Prompt</Label>
                <Textarea value={imagePrompt} onChange={(e) => setImagePrompt(e.target.value)} rows={6} placeholder="z.B. Professioneller Werbeanhänger an stark befahrener Strasse in Wetzikon, photorealistisch" data-testid="media-ai-prompt" />
              </div>
              <Button onClick={generateMedia} disabled={generatingImage} className="w-full" data-testid="media-ai-generate-button">
                {generatingImage ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}KI-Bild erzeugen
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><ImageIcon className="w-5 h-5" /> Bildquellen-Logik</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm text-[hsl(var(--muted-foreground))]">
              <p>1. Beitrag kann ein eigenes Bild oder ein KI-Bild aus der Mediathek erhalten.</p>
              <p>2. Beim Social Draft wählen Sie pro Plattform erneut: Beitragsbild, Mediathek oder ohne Bild.</p>
              <p>3. Admin-Freigabe und Automatik-Modus greifen danach auf dieselben Bildquellen zu.</p>
            </CardContent>
          </Card>

          <Card className="xl:col-span-3">
            <CardHeader>
              <CardTitle>Mediathek</CardTitle>
              <CardDescription>Eigene Fotos und KI-Bilder aus der Datenbank / Liste.</CardDescription>
            </CardHeader>
            <CardContent>
              {mediaAssets.length === 0 ? (
                <div className="text-center py-10 text-[hsl(var(--muted-foreground))]">
                  <ImagePlus className="w-10 h-10 mx-auto mb-3 opacity-50" />
                  <p>Noch keine Medien in der Mediathek.</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                  {mediaAssets.map((asset) => (
                    <div key={asset.id} className="rounded-xl border border-[hsl(var(--border))] overflow-hidden" data-testid={`media-asset-${asset.id}`}>
                      <div className="aspect-[4/3] bg-[hsl(var(--surface-2))] flex items-center justify-center overflow-hidden">
                        {asset.url ? <img src={asset.url} alt={asset.title} className="w-full h-full object-cover" /> : <ImageIcon className="w-8 h-8 text-[hsl(var(--muted-foreground))]" />}
                      </div>
                      <div className="p-4 space-y-2">
                        <div className="flex items-center justify-between gap-2">
                          <p className="font-medium line-clamp-2">{asset.title}</p>
                          <Badge variant="outline">{asset.source_type}</Badge>
                        </div>
                        {asset.prompt && <p className="text-xs text-[hsl(var(--muted-foreground))] line-clamp-3">{asset.prompt}</p>}
                        <div className="flex items-center justify-between gap-2 pt-1">
                          <Button variant="outline" size="sm" onClick={() => setFormData((prev) => ({ ...prev, featured_image: asset.url, featured_media_id: asset.id, featured_image_source: asset.source_type }))} data-testid={`media-use-${asset.id}`}>
                            Verwenden
                          </Button>
                          <Button variant="outline" size="sm" className="text-red-500" onClick={() => deleteMedia(asset.id)} data-testid={`media-delete-${asset.id}`}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {activeTab === 'auto' && (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><Settings className="w-5 h-5" /> Auto-Blog Einstellungen</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between p-4 bg-[hsl(var(--accent)/0.3)] rounded-lg">
                <div>
                  <p className="font-medium text-[hsl(var(--foreground))]">Automatische Blog-Erstellung</p>
                  <p className="text-sm text-[hsl(var(--muted-foreground))]">KI erstellt und veröffentlicht automatisch Artikel. Eine zweite KI prüft die Qualität.</p>
                </div>
                <Switch checked={autoSettings.auto_blog_enabled} onCheckedChange={(checked) => saveAutoSettings(checked, autoSettings.auto_blog_interval_hours)} disabled={savingAuto} data-testid="auto-blog-toggle" />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Frequenz</Label>
                  <Select value={String(autoSettings.auto_blog_interval_hours)} onValueChange={(value) => saveAutoSettings(autoSettings.auto_blog_enabled, parseInt(value, 10))}>
                    <SelectTrigger data-testid="auto-blog-interval"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="24">Täglich</SelectItem>
                      <SelectItem value="48">Alle 2 Tage</SelectItem>
                      <SelectItem value="72">Alle 3 Tage</SelectItem>
                      <SelectItem value="168">Wöchentlich</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Letzter Auto-Beitrag</Label>
                  <p className="text-sm text-[hsl(var(--muted-foreground))] pt-2">{autoSettings.auto_blog_last_generated ? new Date(autoSettings.auto_blog_last_generated).toLocaleString('de-CH') : 'Noch keiner'}</p>
                </div>
              </div>

              <Button variant="outline" onClick={triggerAutoGenerate} disabled={triggering} data-testid="auto-blog-trigger">
                {triggering ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Zap className="w-4 h-4 mr-2" />}Jetzt einen Artikel generieren & veröffentlichen
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><Clock className="w-5 h-5" /> Aktivitätsprotokoll</CardTitle>
            </CardHeader>
            <CardContent>
              {autoLog.length === 0 ? (
                <p className="text-sm text-[hsl(var(--muted-foreground))] py-4 text-center">Noch keine automatischen Aktivitäten.</p>
              ) : (
                <div className="space-y-3">
                  {autoLog.map((log, index) => (
                    <div key={log.id || index} className="flex items-start gap-3 p-3 rounded-lg border border-[hsl(var(--border))]">
                      {log.action === 'published' && <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />}
                      {log.action !== 'published' && <XCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          <p className="text-sm font-medium text-[hsl(var(--foreground))] truncate">{log.title}</p>
                          <Badge variant={log.action === 'published' ? 'default' : 'secondary'} className="text-xs">{log.action}</Badge>
                        </div>
                        <p className="text-xs text-[hsl(var(--muted-foreground))]">{log.reason} · {log.timestamp ? new Date(log.timestamp).toLocaleString('de-CH') : ''}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      <Dialog open={editDialog} onOpenChange={setEditDialog}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editPost ? 'Beitrag bearbeiten' : 'Neuer Beitrag'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Titel *</Label>
              <Input value={formData.title} onChange={(e) => setFormData((prev) => ({ ...prev, title: e.target.value }))} placeholder="SEO-optimierter Titel" data-testid="blog-edit-title" />
            </div>
            <div className="space-y-2">
              <Label>Inhalt (Markdown)</Label>
              <Textarea value={formData.content} onChange={(e) => setFormData((prev) => ({ ...prev, content: e.target.value }))} rows={14} placeholder="## Überschrift&#10;&#10;Ihr Inhalt hier..." className="font-mono text-sm" data-testid="blog-edit-content" />
            </div>
            <div className="space-y-2">
              <Label>Kurzbeschreibung</Label>
              <Textarea value={formData.excerpt} onChange={(e) => setFormData((prev) => ({ ...prev, excerpt: e.target.value }))} rows={2} placeholder="Kurze Zusammenfassung" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Meta-Beschreibung (SEO)</Label>
                <Input value={formData.meta_description} onChange={(e) => setFormData((prev) => ({ ...prev, meta_description: e.target.value }))} placeholder="Max. 155 Zeichen" data-testid="blog-edit-meta" />
              </div>
              <div className="space-y-2">
                <Label>Keywords (SEO)</Label>
                <Input value={formData.meta_keywords} onChange={(e) => setFormData((prev) => ({ ...prev, meta_keywords: e.target.value }))} placeholder="keyword1, keyword2, ..." />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Bildquelle wählen</Label>
                <Select value={formData.featured_media_id || 'manual'} onValueChange={handleImageSourceChange}>
                  <SelectTrigger data-testid="blog-edit-image-source"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="manual">Eigene URL manuell eingeben</SelectItem>
                    {mediaAssets.map((asset) => <SelectItem key={asset.id} value={asset.id}>{asset.title} ({asset.source_type})</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={formData.status} onValueChange={(value) => setFormData((prev) => ({ ...prev, status: value }))}>
                  <SelectTrigger data-testid="blog-edit-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">Entwurf</SelectItem>
                    <SelectItem value="published">Veröffentlicht</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Bild-URL</Label>
              <Input value={formData.featured_image} onChange={(e) => setFormData((prev) => ({ ...prev, featured_image: e.target.value, featured_image_source: 'manual', featured_media_id: '' }))} placeholder="https://... oder /api/uploads/..." data-testid="blog-edit-image-url" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialog(false)}>Abbrechen</Button>
            <Button onClick={savePost} disabled={saving} data-testid="blog-edit-save">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {editPost ? 'Speichern' : 'Erstellen'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={generateDialog} onOpenChange={setGenerateDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Sparkles className="w-5 h-5 text-[hsl(var(--primary))]" /> KI-Artikel generieren</DialogTitle>
            <DialogDescription>KI generiert den Artikel und eine zweite KI prüft die Qualität.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Thema / Titel</Label>
              <Input value={genTopic} onChange={(e) => setGenTopic(e.target.value)} placeholder="z.B. Aussenwerbung in Wetzikon" data-testid="blog-gen-topic" />
            </div>
            <div className="space-y-2">
              <Label>Region</Label>
              <Select value={genRegion} onValueChange={setGenRegion}>
                <SelectTrigger data-testid="blog-gen-region"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {['Zürcher Oberland', 'Wetzikon', 'Uster', 'Pfäffikon', 'Hinwil', 'Bubikon', 'Rüti', 'Volketswil', 'Dübendorf', 'Illnau-Effretikon', 'Gossau ZH', 'Grüningen', 'Bauma', 'Wald'].map((region) => (
                    <SelectItem key={region} value={region}>{region}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="flex items-center gap-1.5"><Lightbulb className="w-3.5 h-3.5" /> Vorschläge</Label>
                <Button variant="ghost" size="sm" onClick={fetchSuggestions} disabled={loadingSuggestions} data-testid="blog-refresh-suggestions">
                  <RefreshCw className={`w-3.5 h-3.5 ${loadingSuggestions ? 'animate-spin' : ''}`} />
                </Button>
              </div>
              <div className="space-y-1.5 max-h-40 overflow-y-auto">
                {loadingSuggestions ? (
                  <div className="flex justify-center py-3"><Loader2 className="w-4 h-4 animate-spin" /></div>
                ) : suggestions.map((item, index) => (
                  <button key={index} onClick={() => { setGenTopic(item.topic); setGenRegion(item.region); }} className={`w-full text-left text-sm px-3 py-2 rounded-md border transition-colors ${genTopic === item.topic ? 'border-[hsl(var(--primary))] bg-[hsl(var(--primary)/0.05)]' : 'border-[hsl(var(--border))] hover:bg-[hsl(var(--accent)/0.3)]'}`} data-testid={`blog-suggestion-${index}`}>{item.topic}</button>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setGenerateDialog(false)}>Abbrechen</Button>
            <Button onClick={generateArticle} disabled={generating || !genTopic.trim()} data-testid="blog-gen-submit">
              {generating ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Wird generiert...</> : <><Sparkles className="w-4 h-4 mr-2" /> Generieren</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={deleteDialog} onOpenChange={setDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Beitrag löschen</DialogTitle>
            <DialogDescription>Möchten Sie diesen Beitrag wirklich löschen?</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog(false)}>Abbrechen</Button>
            <Button variant="destructive" onClick={deletePost} data-testid="blog-delete-confirm">Löschen</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminBlog;